import React from 'react';
import type { FieldConfig, FieldValues } from '../types/models';

interface Props {
  cardFields: FieldConfig[];
  fieldValues: FieldValues;
  isEditing: boolean;
  onChange: (fieldId: string, value: any) => void;
  /** Render as a sub-group inside a caller-owned card (eyebrow label, plain np-field labels,
   * no card chrome of its own). Default false = unchanged for every other caller. */
  embedded?: boolean;
}

const NOTE_META: Record<string, { icon: string; color: string; empty: string }> = {
  comment: { icon: 'ri-file-text-line', color: 'primary', empty: 'No comment recorded.' },
  improvement_needed: { icon: 'ri-alert-line', color: 'warning', empty: 'None flagged for this cycle.' },
};

export const ReviewNotesCard: React.FC<Props> = ({ cardFields, fieldValues, isEditing, onChange, embedded = false }) => {
  // Edit mode is unchanged - still a plain .np-field + textarea per field, same as before.
  // View mode below is new: notes break out of a compact field-row grid entirely into their own
  // full-width block with real body-copy line-height, matching how every premium product
  // researched (Linear/Attio/Notion/GitHub) treats a long-form text field differently from a
  // short scalar value - none of them cram free text into a label/value row built for a date or
  // a short code.
  if (embedded) {
    return (
      <div className="np-details-group">
        <span className="eyebrow-label">Review Notes</span>
        {cardFields.map((config, i) => {
          const meta = NOTE_META[config.fieldId] || { icon: 'ri-sticky-note-line', color: 'secondary', empty: '—' };
          const value = String(fieldValues[config.fieldId] ?? '');
          if (isEditing) {
            return (
              <div key={config.fieldId} className={`np-field${i === cardFields.length - 1 ? ' np-mb-0' : ''}`}>
                <label>{config.fieldName}</label>
                <textarea className="np-input" rows={2} value={value} onChange={(e) => onChange(config.fieldId, e.target.value)} />
              </div>
            );
          }
          return (
            <div key={config.fieldId} className="np-detail-notes-block">
              <span className="np-detail-notes-label">{config.fieldName}</span>
              <div className="np-detail-notes-body">{value.trim() || <span className="np-hint">{meta.empty}</span>}</div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="col-12 mb-4">
      <div className="np-card">
        <div className="np-card-head">
          <h2>Review Notes</h2>
        </div>
        <div className="np-card-body">
          {cardFields.map((config, i) => {
            const meta = NOTE_META[config.fieldId] || { icon: 'ri-sticky-note-line', color: 'secondary', empty: '—' };
            const value = String(fieldValues[config.fieldId] ?? '');
            return (
              <div key={config.fieldId} className={i > 0 ? 'mt-4 pt-4 border-top' : ''}>
                <div className={`d-flex align-items-center gap-2 text-${meta.color} mb-2`}>
                  <i className={meta.icon}></i>
                  <span
                    className="fw-bold"
                    style={{ fontSize: '0.7rem', letterSpacing: '0.05em', textTransform: 'uppercase' }}
                  >
                    {config.fieldName}
                  </span>
                </div>
                {isEditing ? (
                  <textarea
                    className="np-input"
                    rows={2}
                    value={value}
                    onChange={(e) => onChange(config.fieldId, e.target.value)}
                  />
                ) : (
                  <p className="mb-0">{value.trim() || <span className="np-hint">{meta.empty}</span>}</p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
